import React, { useEffect, useMemo, useState } from "react";
import { Alert, Checkbox, Menu, MenuItem, TableSortLabel } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { useNavigate } from "react-router-dom";
import { commonStyles } from "../../../common/commonStyles";
import {
  DataTable,
  DataTableColumn,
} from "../../../common/dashboard/DataTable";
import {
  buildRowActions,
  RowActionMenu,
  RowActionsTrigger,
} from "../../../common/dashboard/RowActionMenu";
import { StatusBadge } from "../../../common/dashboard/StatusBadge";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import { RoutePaths } from "../../../../enumStrings";
import {
  CmpNewsletterStatusEnum,
  MonthEnum,
  useArchiveCmpNewsletterMutation,
  useCreateCmpNewsletterMutation,
  useGetChamberMemberProSettingsQuery,
  useGetCmpManageNewslettersQuery,
  useGetCmpNewsletterDetailLazyQuery,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import CalendarDateInput from "../../MyChamber/ChamberSettings/common/CalendarDateInput";
import {
  buildEmailDuplicateDraft,
  emailFormStateToInput,
  manageNewsletterRowToEmailCampaign,
  newsletterReturnToFormState,
  newsroomStatusToApi,
  statusToApi,
} from "../emailBackendMappers";
import {
  EmailCampaign,
  EmailStatus,
  EmailType,
  NewsroomStatus,
  emailTypeOptions,
  getEmailTypeLabel,
  getStatusLabel,
} from "../emailMockData";

type ColumnKey =
  | "type"
  | "name"
  | "subject"
  | "date"
  | "recipients"
  | "openRate"
  | "clickRate"
  | "status";
type SortDirection = "asc" | "desc";
// Non-sortable trailing Actions column for the shared DataTable (B12 /
// POL-003 final leg).
type EmailsTableColumnKey = ColumnKey | "actions";

type EmailActionKey =
  | "preview"
  | "edit"
  | "duplicate"
  | "schedule"
  | "viewAnalytics"
  | "viewOpenList"
  | "delete";

// Menu items and order are BINDING per the Figma annotation: 7 items, a
// divider before Delete, icons only on Edit and Delete, red Delete. Every
// status gets the same ordered list; draft rows hide View Analytics
// (EMAIL-MANAGE-005).
const emailActionOrder: ReadonlyArray<EmailActionKey> = [
  "preview",
  "edit",
  "duplicate",
  "schedule",
  "viewAnalytics",
  "viewOpenList",
  "delete",
];

const getEmailRowActions = buildRowActions<
  EmailStatus,
  EmailActionKey,
  EmailCampaign
>({
  matrix: {
    draft: emailActionOrder,
    review: emailActionOrder,
    scheduled: emailActionOrder,
    sending: ["preview", "duplicate", "viewAnalytics", "viewOpenList"],
    sent: emailActionOrder,
    canceled: emailActionOrder,
    failed: emailActionOrder,
  },
  labels: {
    preview: "Preview Communication",
    edit: "Edit Communication",
    duplicate: "Duplicate Communication (attachments excluded)",
    schedule: "Schedule Communication",
    viewAnalytics: "View Analytics",
    viewOpenList: "View Subscriber Email Open List",
    delete: "Delete",
  },
  getStatus: (email) => email.status,
  destructiveActions: new Set<EmailActionKey>(["delete"]),
  dividerBeforeActions: new Set<EmailActionKey>(["delete"]),
  // Draft rows hide View Analytics (EMAIL-MANAGE-005).
  isHidden: (action, email) =>
    action === "viewAnalytics" && email.status === "draft",
  // Annotation: only Edit and Delete carry icons.
  getIcon: (action) =>
    action === "edit" ? (
      <EditOutlinedIcon fontSize="small" className="mr-3" />
    ) : action === "delete" ? (
      <DeleteOutlineIcon fontSize="small" className="mr-3" />
    ) : undefined,
});

// Transcription of the page's previous hand-rolled Actions trigger button
// (h-38 white bordered pill with the small caret).
const emailsActionButtonSx = {
  backgroundColor: "white",
  borderColor: "#E5E7EB",
  borderRadius: "6px",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  color: "#374151",
  fontSize: 14,
  fontWeight: 800,
  gap: 0.5,
  minHeight: 38,
  px: 2,
  textTransform: "none",
};

const statusFilterOptions: EmailStatus[] = [
  "draft",
  "review",
  "scheduled",
  "sending",
  "sent",
  "canceled",
  "failed",
];
const newsroomFilterOptions: NewsroomStatus[] = [
  "notPublished",
  "published",
  "unpublished",
];
const allEmailTypeValues: EmailType[] = emailTypeOptions.map(
  (option) => option.value
);

const inputClassName =
  "h-[44px] rounded-md border border-gray-300 bg-white px-4 text-[15px] text-gray-950 outline-none transition placeholder:text-gray-400 focus:border-cpaGold focus:ring-2 focus:ring-cpaGold/20";
const filterButtonClassName =
  "inline-flex h-[44px] items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-5 text-[14px] font-semibold text-gray-500 transition hover:bg-gray-50";
const goldButtonClassName =
  "inline-flex h-[44px] items-center justify-center gap-2 rounded-md border border-cpaGold bg-cpaGold px-5 text-[14px] font-extrabold text-white transition hover:bg-cpaGoldDark disabled:cursor-not-allowed disabled:opacity-60";

// Checked checkboxes are CMP gold platform-wide (Q-008 operator answer).
const checkedCheckboxSx = {
  "&.Mui-checked": { color: commonStyles.cpaGold },
};

// Same paper treatment as the ManageInvoices/ManageMembers date-range menus:
// overflow visible so the CalendarDateInput dropdown can extend past the menu
// surface instead of being clipped.
const dateRangeMenuPaperSx = {
  borderRadius: "8px",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.16)",
  mt: 0.75,
  overflow: "visible",
  p: 2,
  width: 320,
};

const formatNumber = (value: number) => value.toLocaleString();

const getRate = (numerator: number, denominator: number) =>
  denominator ? Math.round((numerator / denominator) * 100) : 0;

const getRateOneDecimal = (numerator: number, denominator: number) =>
  denominator ? Math.round((numerator / denominator) * 1000) / 10 : 0;

const getDateInputStartUtcMilli = (value: string) =>
  value ? new Date(`${value}T00:00:00`).getTime() : null;

const getDateInputEndUtcMilli = (value: string) =>
  value ? new Date(`${value}T23:59:59.999`).getTime() : null;

const formatDateInputLabel = (value: string) =>
  value
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(new Date(`${value}T00:00:00`))
    : "";

const getDateRangeFilterLabel = (fromValue: string, toValue: string) => {
  if (fromValue && toValue) {
    return `${formatDateInputLabel(fromValue)} - ${formatDateInputLabel(toValue)}`;
  }

  if (fromValue) {
    return `From ${formatDateInputLabel(fromValue)}`;
  }

  if (toValue) {
    return `Through ${formatDateInputLabel(toValue)}`;
  }

  return "Filter by Date Range";
};

const matchesSentDateRange = (
  sentDateMilli: number | null | undefined,
  fromValue: string,
  toValue: string
) => {
  if (!fromValue && !toValue) {
    return true;
  }

  if (typeof sentDateMilli !== "number") {
    return false;
  }

  const fromMilli = getDateInputStartUtcMilli(fromValue);
  const toMilli = getDateInputEndUtcMilli(toValue);
  const startMilli =
    fromMilli !== null && toMilli !== null
      ? Math.min(fromMilli, toMilli)
      : fromMilli;
  const endMilli =
    fromMilli !== null && toMilli !== null
      ? Math.max(fromMilli, toMilli)
      : toMilli;

  if (startMilli !== null && sentDateMilli < startMilli) {
    return false;
  }

  if (endMilli !== null && sentDateMilli > endMilli) {
    return false;
  }

  return true;
};

const monthIndexByEnum: Record<MonthEnum, number> = {
  [MonthEnum.January]: 0,
  [MonthEnum.February]: 1,
  [MonthEnum.March]: 2,
  [MonthEnum.April]: 3,
  [MonthEnum.May]: 4,
  [MonthEnum.June]: 5,
  [MonthEnum.July]: 6,
  [MonthEnum.August]: 7,
  [MonthEnum.September]: 8,
  [MonthEnum.October]: 9,
  [MonthEnum.November]: 10,
  [MonthEnum.December]: 11,
};

const getFiscalYearWindows = ({
  fiscalYearStartDay,
  fiscalYearStartMonthIndex,
  nowMilli,
}: {
  fiscalYearStartDay: number;
  fiscalYearStartMonthIndex: number;
  nowMilli: number;
}) => {
  const now = new Date(nowMilli);
  const currentStart = new Date(
    now.getFullYear(),
    fiscalYearStartMonthIndex,
    fiscalYearStartDay,
    0,
    0,
    0,
    0
  );

  if (currentStart.getTime() > nowMilli) {
    currentStart.setFullYear(currentStart.getFullYear() - 1);
  }

  const previousStart = new Date(currentStart.getTime());
  previousStart.setFullYear(previousStart.getFullYear() - 1);
  const elapsedMilli = nowMilli - currentStart.getTime();

  return {
    currentStartMilli: currentStart.getTime(),
    currentEndMilli: nowMilli,
    previousStartMilli: previousStart.getTime(),
    previousEndMilli: previousStart.getTime() + elapsedMilli,
  };
};

type FiscalWindowRow = {
  clickCount: number;
  openCount: number;
  sentAtUtcMilli?: number | null;
  sentRecipientCount: number;
  status: CmpNewsletterStatusEnum;
};

const summarizeSentWindow = (
  rows: FiscalWindowRow[],
  startMilli: number,
  endMilli: number
) => {
  const sentRows = rows.filter(
    (row) =>
      row.status === CmpNewsletterStatusEnum.Sent &&
      typeof row.sentAtUtcMilli === "number" &&
      row.sentAtUtcMilli >= startMilli &&
      row.sentAtUtcMilli <= endMilli
  );
  const recipientsEmailed = sentRows.reduce(
    (total, row) => total + row.sentRecipientCount,
    0
  );
  const openCount = sentRows.reduce((total, row) => total + row.openCount, 0);
  const clickCount = sentRows.reduce((total, row) => total + row.clickCount, 0);

  return {
    campaignsSent: sentRows.length,
    recipientsEmailed,
    averageOpenRate: getRateOneDecimal(openCount, recipientsEmailed),
    averageClickRate: getRateOneDecimal(clickCount, recipientsEmailed),
  };
};

// Green trend text renders ONLY when real prior-fiscal-year data exists
// (EMAIL-MANAGE-001). No prior-year rows -> no trend line at all.
const getYearOverYearTrend = (currentValue: number, previousValue: number) => {
  if (previousValue <= 0) {
    return null;
  }

  const changePercent = Math.round(
    (Math.abs(currentValue - previousValue) / previousValue) * 100
  );

  if (changePercent === 0) {
    return null;
  }

  const increased = currentValue > previousValue;

  // The mock renders every trend line GREEN — including decreases (e.g.
  // Unsubscribes "2% decrease from last year") — so direction only changes
  // the wording, never the color.
  return {
    className: "text-emerald-500",
    text: `${changePercent}% ${increased ? "increase" : "decrease"} from last year`,
  };
};

const getSortValue = (email: EmailCampaign, column: ColumnKey) => {
  switch (column) {
    case "type":
      return getEmailTypeLabel(email.emailType);
    case "name":
      return email.category || email.subject;
    case "subject":
      return email.subject;
    case "date":
      return email.sentAt || email.scheduledAt || email.updatedAt;
    case "recipients":
      return email.intendedRecipients;
    case "openRate":
      return getRate(email.openCount, email.sentRecipients);
    case "clickRate":
      return getRate(email.clickCount, email.sentRecipients);
    case "status":
      return getStatusLabel(email.status);
    default:
      return "";
  }
};

const AnalyticsCard = ({
  label,
  value,
  trend,
  note,
}: {
  label: string;
  value: string;
  trend?: { className: string; text: string } | null;
  note?: string;
}) => (
  <div className="min-h-[132px] rounded-lg border border-gray-300 bg-white px-5 py-5">
    <p className="text-[14px] font-bold leading-5 text-gray-950">{label}</p>
    <p className="mt-3 text-2xl font-bold leading-none text-gray-900">
      {value}
    </p>
    {trend ? (
      <p className={`mt-4 text-sm font-bold ${trend.className}`}>
        {trend.text}
      </p>
    ) : note ? (
      <p className="mt-4 text-[13px] font-semibold text-gray-500">{note}</p>
    ) : null}
  </div>
);

const RateMeter = ({
  numerator,
  denominator,
  tone,
}: {
  numerator: number;
  denominator: number;
  tone: "green" | "gold";
}) => {
  const percentage = getRate(numerator, denominator);
  // 0% open/click renders RED with an empty bar (EMAIL-MANAGE-004); non-zero
  // values keep the screenshot colors (green open rate, gold click rate).
  const color =
    percentage === 0
      ? "#EF4444"
      : tone === "green"
      ? "#10B981"
      : commonStyles.cpaGold;

  return (
    <div className="min-w-[104px]">
      <p className="text-[14px] font-extrabold text-gray-950">
        {formatNumber(numerator)}/{formatNumber(denominator)}
      </p>
      <div className="mt-2 h-2.5 rounded-full bg-gray-100">
        {percentage > 0 ? (
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(Math.max(percentage, 4), 100)}%`,
              backgroundColor: color,
            }}
          />
        ) : null}
      </div>
      <p className="mt-1 text-[13px] font-extrabold" style={{ color }}>
        {percentage}%
      </p>
    </div>
  );
};

const statusTextColor = (status: EmailStatus) => {
  switch (status) {
    case "sent":
      return "text-emerald-700";
    case "scheduled":
      return "text-amber-700";
    case "sending":
      return "text-blue-700";
    case "failed":
      return "text-red-600";
    default:
      return "text-gray-950";
  }
};

const buildCsv = (emails: EmailCampaign[], kind: "all" | "opens" | "clicks") => {
  const headers =
    kind === "all"
      ? [
          "Communication Type",
          "Campaign Name",
          "Email Subject",
          "Sent Date",
          "Recipients",
          "Open Count",
          "Click Count",
          "Status",
        ]
      : ["Campaign Name", kind === "opens" ? "Open Count" : "Click Count", "Recipients"];
  const rows = emails.map((email) =>
    kind === "all"
      ? [
          getEmailTypeLabel(email.emailType),
          email.category || email.subject,
          email.subject,
          email.sentAt || email.scheduledAt || "",
          email.intendedRecipients,
          email.openCount,
          email.clickCount,
          getStatusLabel(email.status),
        ]
      : [
          email.category || email.subject,
          kind === "opens" ? email.openCount : email.clickCount,
          email.sentRecipients,
        ]
  );

  return [headers, ...rows]
    .map((row) =>
      row
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");
};

const downloadCsv = (
  emails: EmailCampaign[],
  kind: "all" | "opens" | "clicks",
  fileName: string
) => {
  const blob = new Blob([buildCsv(emails, kind)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const ManageEmails = () => {
  const navigate = useNavigate();
  const setPageHeaderOverride = usePageHeaderOverride();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [searchQuery, setSearchQuery] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [selectedEmailTypes, setSelectedEmailTypes] =
    useState<EmailType[]>(allEmailTypeValues);
  const [selectedStatuses, setSelectedStatuses] =
    useState<EmailStatus[]>(statusFilterOptions);
  const [sentDateFromFilter, setSentDateFromFilter] = useState("");
  const [sentDateToFilter, setSentDateToFilter] = useState("");
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<HTMLElement | null>(null);
  const [audienceMenuAnchor, setAudienceMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [tagsMenuAnchor, setTagsMenuAnchor] = useState<HTMLElement | null>(null);
  const [dateMenuAnchor, setDateMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedEmailIds, setSelectedEmailIds] = useState<string[]>([]);
  // RowActionMenu usage contract: a SINGLE page-level menu instance anchored
  // via { row, anchorEl } (ManageEvents donor pattern).
  const [actionMenu, setActionMenu] = useState<{
    email: EmailCampaign;
    el: HTMLElement;
  } | null>(null);
  const [pendingDeleteEmail, setPendingDeleteEmail] =
    useState<EmailCampaign | null>(null);
  const [sortColumn, setSortColumn] = useState<ColumnKey>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [statusMessage, setStatusMessage] = useState<{
    severity: "success" | "info" | "warning" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    setPageHeaderOverride({
      title: "Manage Emails",
      description:
        "View email performance, audience engagement, delivery status, and campaign analytics all from one location",
      titleClassName: "text-2xl font-bold text-black",
      descriptionClassName: "mt-2 whitespace-pre-line text-lg text-black",
      actions: (
        <button
          type="button"
          className={goldButtonClassName}
          onClick={() => navigate(RoutePaths.chamberAdmin_emails_create)}
        >
          <AddIcon />
          Create New Email Communication
        </button>
      ),
    });

    return () => setPageHeaderOverride(null);
  }, [navigate, setPageHeaderOverride]);

  const hasStatusFilter =
    selectedStatuses.length !== statusFilterOptions.length;
  const hasEmptyStatusSelection = hasStatusFilter && selectedStatuses.length === 0;
  const statusesForQuery = hasStatusFilter
    ? selectedStatuses.map(statusToApi)
    : statusFilterOptions.map(statusToApi);
  const hasTypeFilter = selectedEmailTypes.length !== allEmailTypeValues.length;

  const {
    data: newslettersData,
    loading: newslettersLoading,
    refetch: refetchNewsletters,
  } = useGetCmpManageNewslettersQuery({
    variables: {
      input: {
        chamberId,
        search: searchQuery.trim() || undefined,
        statuses: statusesForQuery,
        newsroomStatuses: newsroomFilterOptions.map(newsroomStatusToApi),
      },
    },
    skip: !chamberId || hasEmptyStatusSelection,
    fetchPolicy: "cache-and-network",
  });
  // Separate unfiltered pull so the fiscal-YTD analytics cards stay stable
  // while the table filters/search change (honest whole-chamber stats).
  const { data: analyticsQueryData } = useGetCmpManageNewslettersQuery({
    variables: {
      input: {
        chamberId,
        statuses: statusFilterOptions.map(statusToApi),
        newsroomStatuses: newsroomFilterOptions.map(newsroomStatusToApi),
      },
    },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  // Fiscal-year boundaries come from Chamber Setup (Global Settings).
  const { data: settingsData } = useGetChamberMemberProSettingsQuery({
    variables: { chamberId },
    skip: !chamberId,
  });
  const [getCmpNewsletterDetail] = useGetCmpNewsletterDetailLazyQuery({
    fetchPolicy: "network-only",
  });
  const [createCmpNewsletter] = useCreateCmpNewsletterMutation();
  const [archiveCmpNewsletter] = useArchiveCmpNewsletterMutation();

  const emails = useMemo(
    () =>
      hasEmptyStatusSelection
        ? []
        : (newslettersData?.getCmpManageNewsletters.newsletters || []).map(
            manageNewsletterRowToEmailCampaign
          ),
    [hasEmptyStatusSelection, newslettersData?.getCmpManageNewsletters.newsletters]
  );

  // Raw millis for the Sent date column so the date-range filter can compare
  // real timestamps instead of parsing display strings.
  const sentDateMilliByEmailId = useMemo(() => {
    const milliById = new Map<string, number | null>();

    (newslettersData?.getCmpManageNewsletters.newsletters || []).forEach((row) => {
      milliById.set(row.id, row.sentAtUtcMilli ?? row.scheduledAtUtcMilli ?? null);
    });

    return milliById;
  }, [newslettersData?.getCmpManageNewsletters.newsletters]);

  const filteredEmails = useMemo(
    () =>
      emails.filter((email) => {
        const subjectMatch =
          !subjectFilter.trim() ||
          email.subject.toLowerCase().includes(subjectFilter.trim().toLowerCase());
        const typeMatch =
          !hasTypeFilter || selectedEmailTypes.includes(email.emailType);
        const dateMatch = matchesSentDateRange(
          sentDateMilliByEmailId.get(email.id),
          sentDateFromFilter,
          sentDateToFilter
        );

        return subjectMatch && typeMatch && dateMatch;
      }),
    [
      emails,
      hasTypeFilter,
      selectedEmailTypes,
      sentDateFromFilter,
      sentDateMilliByEmailId,
      sentDateToFilter,
      subjectFilter,
    ]
  );

  const sortedEmails = useMemo(() => {
    return [...filteredEmails].sort((firstEmail, secondEmail) => {
      const firstValue = getSortValue(firstEmail, sortColumn);
      const secondValue = getSortValue(secondEmail, sortColumn);
      const directionMultiplier = sortDirection === "asc" ? 1 : -1;

      if (typeof firstValue === "number" && typeof secondValue === "number") {
        return (firstValue - secondValue) * directionMultiplier;
      }

      return (
        String(firstValue).localeCompare(String(secondValue)) *
        directionMultiplier
      );
    });
  }, [filteredEmails, sortColumn, sortDirection]);

  const analyticsRows = useMemo(
    () => analyticsQueryData?.getCmpManageNewsletters.newsletters || [],
    [analyticsQueryData?.getCmpManageNewsletters.newsletters]
  );

  const analytics = useMemo(() => {
    const setup = settingsData?.getChamberMemberProSettings.setup;
    const windows = getFiscalYearWindows({
      fiscalYearStartDay: setup?.fiscalYearStartDay || 1,
      fiscalYearStartMonthIndex: setup
        ? monthIndexByEnum[setup.fiscalYearStartMonth]
        : 0,
      nowMilli: Date.now(),
    });

    return {
      currentYtd: summarizeSentWindow(
        analyticsRows,
        windows.currentStartMilli,
        windows.currentEndMilli
      ),
      previousYtd: summarizeSentWindow(
        analyticsRows,
        windows.previousStartMilli,
        windows.previousEndMilli
      ),
      draftEmails: analyticsRows.filter(
        (row) => row.status === CmpNewsletterStatusEnum.Draft
      ).length,
      scheduledEmails: analyticsRows.filter(
        (row) => row.status === CmpNewsletterStatusEnum.Scheduled
      ).length,
    };
  }, [analyticsRows, settingsData?.getChamberMemberProSettings.setup]);

  const updateSort = (column: ColumnKey) => {
    if (sortColumn === column) {
      setSortDirection((currentDirection) =>
        currentDirection === "asc" ? "desc" : "asc"
      );
      return;
    }
    setSortColumn(column);
    setSortDirection(
      column === "subject" || column === "name" || column === "type"
        ? "asc"
        : "desc"
    );
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSubjectFilter("");
    setSelectedEmailTypes(allEmailTypeValues);
    setSelectedStatuses(statusFilterOptions);
    setSentDateFromFilter("");
    setSentDateToFilter("");
  };

  const toggleEmailType = (emailType: EmailType) => {
    setSelectedEmailTypes((currentTypes) =>
      currentTypes.includes(emailType)
        ? currentTypes.filter((currentType) => currentType !== emailType)
        : [...currentTypes, emailType]
    );
  };

  const toggleStatus = (status: EmailStatus) => {
    setSelectedStatuses((currentStatuses) =>
      currentStatuses.includes(status)
        ? currentStatuses.filter((currentStatus) => currentStatus !== status)
        : [...currentStatuses, status]
    );
  };

  const allVisibleSelected =
    sortedEmails.length > 0 &&
    sortedEmails.every((email) => selectedEmailIds.includes(email.id));

  const toggleSelectAllVisible = () => {
    setSelectedEmailIds(
      allVisibleSelected ? [] : sortedEmails.map((email) => email.id)
    );
  };

  const toggleEmailSelected = (emailId: string) => {
    setSelectedEmailIds((currentIds) =>
      currentIds.includes(emailId)
        ? currentIds.filter((currentId) => currentId !== emailId)
        : [...currentIds, emailId]
    );
  };

  const runNewsletterAction = async (
    action: () => Promise<void>,
    successMessage: string
  ) => {
    try {
      await action();
      await refetchNewsletters();
      setStatusMessage({ severity: "success", message: successMessage });
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "The email action could not be completed.",
      });
    }
  };

  const openEmail = (email: EmailCampaign) => {
    // RowActionMenu closes itself before onAction runs, so no menu cleanup is
    // needed here.
    navigate(RoutePaths.chamberAdmin_emails_update.replace(":emailId", email.id));
  };

  const duplicateAsDraft = async (email: EmailCampaign) => {
    await runNewsletterAction(async () => {
      const detail = await getCmpNewsletterDetail({
        variables: { input: { chamberId, newsletterId: email.id } },
      });
      const newsletter = detail.data?.getCmpNewsletterDetail.newsletter;
      if (!newsletter) throw new Error("The email could not be loaded for duplication.");
      const form = newsletterReturnToFormState(newsletter);
      const result = await createCmpNewsletter({
        variables: {
          input: {
            chamberId,
            data: {
              ...emailFormStateToInput(buildEmailDuplicateDraft(form), chamber),
              status: CmpNewsletterStatusEnum.Draft,
            },
          },
        },
      });
      if (!result.data?.createCmpNewsletter.newsletter?.id) {
        throw new Error("The duplicate draft could not be saved.");
      }
    }, "Email duplicated as a backend draft without attachments.");
  };

  const archiveEmail = async (email: EmailCampaign) => {
    await runNewsletterAction(async () => {
      await archiveCmpNewsletter({
        variables: { input: { chamberId, newsletterId: email.id } },
      });
    }, "Email archived.");
  };

  // B12 (POL-003 final leg): shared DataTable columns. Each render() carries
  // the old <td>'s inner markup verbatim, with the td-level text classes
  // moved onto a wrapper span/div so MUI TableCell typography cannot leak in.
  const emailColumns: Array<
    DataTableColumn<EmailCampaign, EmailsTableColumnKey>
  > = [
    {
      key: "type",
      header: "Communication Type",
      sortable: true,
      width: "160px",
      render: (email) => (
        <span className="text-[14px] font-extrabold text-gray-950">
          {getEmailTypeLabel(email.emailType)}
        </span>
      ),
    },
    {
      key: "name",
      header: "Campaign Name",
      sortable: true,
      width: "160px",
      render: (email) => (
        <div className="max-w-[260px] text-[14px] font-extrabold leading-6 text-gray-950">
          {email.category || email.subject}
        </div>
      ),
    },
    {
      key: "subject",
      header: "Email Subject",
      sortable: true,
      width: "180px",
      render: (email) => (
        <div className="max-w-[260px] text-[14px] font-extrabold leading-6 text-gray-950">
          {email.subject}
        </div>
      ),
    },
    {
      key: "date",
      header: "Sent date",
      sortable: true,
      width: "120px",
      render: (email) => (
        <span className="text-[14px] font-extrabold text-gray-950">
          {email.sentAt || email.scheduledAt || "Not scheduled"}
        </span>
      ),
    },
    {
      key: "recipients",
      header: "Recipients",
      sortable: true,
      width: "125px",
      render: (email) => (
        <span className="text-[14px] font-extrabold text-gray-950">
          {email.intendedRecipients > 0
            ? `${formatNumber(email.intendedRecipients)} Recipients`
            : "No recipients"}
        </span>
      ),
    },
    {
      key: "openRate",
      header: "Open Rate",
      sortable: true,
      width: "110px",
      render: (email) => (
        <RateMeter
          numerator={email.openCount}
          denominator={email.sentRecipients || email.intendedRecipients}
          tone="green"
        />
      ),
    },
    {
      key: "clickRate",
      header: "Click Rate",
      sortable: true,
      width: "110px",
      render: (email) => (
        <RateMeter
          numerator={email.clickCount}
          denominator={email.sentRecipients || email.intendedRecipients}
          tone="gold"
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      width: "100px",
      render: (email) => (
        <StatusBadge
          variant="text"
          label={getStatusLabel(email.status)}
          textClassName={`text-[14px] font-extrabold ${statusTextColor(
            email.status
          )}`}
        />
      ),
    },
    {
      key: "actions",
      header: (
        <span className="text-[14px] font-extrabold text-gray-950">
          Actions
        </span>
      ),
      width: "132px",
      stickyRight: true,
      render: (email) => (
        <RowActionsTrigger
          onOpen={(el) => setActionMenu({ email, el })}
          sx={emailsActionButtonSx}
        />
      ),
    },
  ];

  return (
    <div className="-mx-8 w-[calc(100%+4rem)] border-t border-gray-200 bg-gray-50 px-8 pb-12 pt-10">
      {statusMessage ? (
        <Alert
          severity={statusMessage.severity}
          className="mb-6"
          onClose={() => setStatusMessage(null)}
        >
          {statusMessage.message}
        </Alert>
      ) : null}

      <section className="mb-10">
        <h2 className="text-lg font-bold text-gray-950">
          Total Member Analytics (Fiscal Year to Date)
        </h2>
        <p className="mt-2 text-[15px] text-black">
          See a quick overview of your chamber total year to date email analytics at a glance.
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-7">
          <AnalyticsCard
            label="Total Email Campaigns Sent YTD"
            value={formatNumber(analytics.currentYtd.campaignsSent)}
            trend={getYearOverYearTrend(
              analytics.currentYtd.campaignsSent,
              analytics.previousYtd.campaignsSent
            )}
          />
          <AnalyticsCard
            label="Total Recipients Emails Sent YTD"
            value={formatNumber(analytics.currentYtd.recipientsEmailed)}
            trend={getYearOverYearTrend(
              analytics.currentYtd.recipientsEmailed,
              analytics.previousYtd.recipientsEmailed
            )}
          />
          <AnalyticsCard
            label="Average Open Rate YTD"
            value={`${analytics.currentYtd.averageOpenRate}%`}
            trend={getYearOverYearTrend(
              analytics.currentYtd.averageOpenRate,
              analytics.previousYtd.averageOpenRate
            )}
          />
          <AnalyticsCard
            label="Average Click Rate YTD"
            value={`${analytics.currentYtd.averageClickRate}%`}
            trend={getYearOverYearTrend(
              analytics.currentYtd.averageClickRate,
              analytics.previousYtd.averageClickRate
            )}
          />
          <AnalyticsCard
            label="Unsubscribes YTD"
            value="—"
            note="Not tracked yet"
          />
          <AnalyticsCard label="Draft Emails" value={formatNumber(analytics.draftEmails)} />
          <AnalyticsCard
            label="Scheduled Emails"
            value={formatNumber(analytics.scheduledEmails)}
          />
        </div>
      </section>

      <section className="mb-9">
        <h3 className="text-lg font-bold text-gray-950">
          Communication Filters
        </h3>
        <p className="mt-2 text-[14px] text-black">
          Filter communications by communication type, status, audience, category, tags, or date range.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[360px] flex-1">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className={`${inputClassName} w-full pl-12`}
              placeholder="Enter campaign name"
            />
          </div>
          <input
            value={subjectFilter}
            onChange={(event) => setSubjectFilter(event.target.value)}
            className={`${inputClassName} w-[260px]`}
            placeholder="Filter by Email Subject"
          />
          <button
            type="button"
            className={filterButtonClassName}
            onClick={(event) => setTypeMenuAnchor(event.currentTarget)}
          >
            <TuneOutlinedIcon fontSize="small" />
            Filter by Communication Type
          </button>
          <button
            type="button"
            className="ml-auto inline-flex h-[44px] items-center justify-center rounded-md border border-red-500 bg-red-500 px-6 text-[14px] font-extrabold text-white transition hover:bg-red-600"
            onClick={clearFilters}
          >
            {/* POL-013 (Q-026 answered: plural platform-wide). */}
            Clear All Filters
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-3">
          <button
            type="button"
            className={filterButtonClassName}
            onClick={(event) => setAudienceMenuAnchor(event.currentTarget)}
          >
            <TuneOutlinedIcon fontSize="small" />
            Filter by Audience
          </button>
          <button
            type="button"
            className={filterButtonClassName}
            onClick={(event) => setStatusMenuAnchor(event.currentTarget)}
          >
            <TuneOutlinedIcon fontSize="small" />
            Filter by Status
          </button>
          <button
            type="button"
            className={filterButtonClassName}
            onClick={(event) => setTagsMenuAnchor(event.currentTarget)}
          >
            <TuneOutlinedIcon fontSize="small" />
            Filter by Tags
          </button>
          <button
            type="button"
            className={filterButtonClassName}
            onClick={(event) => setDateMenuAnchor(event.currentTarget)}
          >
            <CalendarMonthIcon fontSize="small" />
            {getDateRangeFilterLabel(sentDateFromFilter, sentDateToFilter)}
          </button>
        </div>
      </section>

      <Menu
        anchorEl={typeMenuAnchor}
        open={Boolean(typeMenuAnchor)}
        onClose={() => setTypeMenuAnchor(null)}
      >
        {emailTypeOptions.map((option) => (
          <MenuItem key={option.value} onClick={() => toggleEmailType(option.value)}>
            <Checkbox
              checked={selectedEmailTypes.includes(option.value)}
              sx={checkedCheckboxSx}
            />
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
      >
        {statusFilterOptions.map((status) => (
          <MenuItem key={status} onClick={() => toggleStatus(status)}>
            <Checkbox
              checked={selectedStatuses.includes(status)}
              sx={checkedCheckboxSx}
            />
            {getStatusLabel(status)}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={audienceMenuAnchor}
        open={Boolean(audienceMenuAnchor)}
        onClose={() => setAudienceMenuAnchor(null)}
      >
        {/* Honest empty state: manage rows carry recipient counts only, so no
            real audience names exist to filter by yet. */}
        <MenuItem disabled sx={{ maxWidth: 320, whiteSpace: "normal" }}>
          Audience details are not available for these emails yet.
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={tagsMenuAnchor}
        open={Boolean(tagsMenuAnchor)}
        onClose={() => setTagsMenuAnchor(null)}
      >
        {/* Honest empty state: tags are not stored on manage rows yet. */}
        <MenuItem disabled sx={{ maxWidth: 320, whiteSpace: "normal" }}>
          Tags are not available for these emails yet.
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => setDateMenuAnchor(null)}
        PaperProps={{ sx: dateRangeMenuPaperSx }}
      >
        <div className="space-y-3">
          <div>
            <div className="text-sm font-extrabold leading-5 text-gray-900">
              Sent date range
            </div>
            <div className="mt-0.5 text-xs font-medium leading-5 text-gray-500">
              Select one date or a from/to range.
            </div>
          </div>
          <div>
            <p className="mb-1 text-sm font-bold text-black">From</p>
            <CalendarDateInput
              ariaLabel="Sent date from"
              mode="fullDate"
              placeholder="Select Date"
              iconPosition="right"
              value={sentDateFromFilter}
              onChange={setSentDateFromFilter}
            />
          </div>
          <div>
            <p className="mb-1 text-sm font-bold text-black">To</p>
            <CalendarDateInput
              ariaLabel="Sent date to"
              mode="fullDate"
              placeholder="Select Date"
              iconPosition="right"
              value={sentDateToFilter}
              onChange={setSentDateToFilter}
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              disabled={!sentDateFromFilter && !sentDateToFilter}
              onClick={() => {
                setSentDateFromFilter("");
                setSentDateToFilter("");
              }}
              className="rounded-md px-3 py-1.5 text-[13px] font-extrabold text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Clear dates
            </button>
            <button
              type="button"
              onClick={() => setDateMenuAnchor(null)}
              className="rounded-md bg-cpaGold px-4 py-1.5 text-[13px] font-extrabold text-white transition hover:bg-cpaGoldDark"
            >
              Done
            </button>
          </div>
        </div>
      </Menu>

      <section>
        <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            {/* Q-028 (binding operator answer, superseding EMAIL-MANAGE-005.6
                and PAR-EA-64): the mock reused the Members-page block [sic];
                shipped copy is Emails-specific. */}
            <h3 className="text-lg font-bold text-gray-950">Email List</h3>
            <p className="mt-2 text-[14px] text-gray-700">Preview and edit email campaigns.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={goldButtonClassName}
              onClick={() =>
                downloadCsv(sortedEmails, "all", "email-communication-report.csv")
              }
            >
              <FileUploadOutlinedIcon />
              Export Email Communication Report
            </button>
            <button
              type="button"
              className={goldButtonClassName}
              onClick={() => downloadCsv(sortedEmails, "opens", "email-open-rate.csv")}
            >
              <FileUploadOutlinedIcon />
              Export Open Rate
            </button>
            <button
              type="button"
              className={goldButtonClassName}
              onClick={() => downloadCsv(sortedEmails, "clicks", "email-click-rate.csv")}
            >
              <FileUploadOutlinedIcon />
              Export Click Rate
            </button>
          </div>
        </div>

        {newslettersLoading ? (
          <div className="mb-4 rounded-md border border-gray-200 bg-white px-5 py-4 text-sm font-semibold text-gray-600">
            Loading emails...
          </div>
        ) : null}

        <DataTable
          ariaLabel="Email communications"
          columns={emailColumns}
          rows={sortedEmails}
          getRowKey={(email) => email.id}
          sort={{
            column: sortColumn,
            direction: sortDirection,
            // updateSort keeps the page-owned toggle rules (text columns
            // start asc, metric/date columns start desc).
            onRequestSort: (column) => updateSort(column as ColumnKey),
          }}
          // Escape hatch reproducing this page's header byte-for-byte: the
          // up/down double-caret sort icon stays visible on every sortable
          // header (mock shows it on all columns except Actions).
          renderSortLabel={({ column, label, active, direction, onRequestSort }) => (
            <TableSortLabel
              active={active}
              direction={active ? direction : "asc"}
              onClick={() => onRequestSort(column)}
              IconComponent={UnfoldMoreIcon}
              sx={{
                "& .MuiTableSortLabel-icon": { color: "#9CA3AF", opacity: 1 },
                alignItems: "center",
                lineHeight: 1.2,
                whiteSpace: "normal",
              }}
            >
              <span className="whitespace-normal text-left text-[14px] font-bold leading-5 text-gray-950">
                {label}
              </span>
            </TableSortLabel>
          )}
          selection={{
            isRowSelected: (email) => selectedEmailIds.includes(email.id),
            onToggleRow: (email) => toggleEmailSelected(email.id),
            onToggleAll: toggleSelectAllVisible,
            allSelected: allVisibleSelected,
            // fontSize 20 bakes in the size="small" the old cells used
            // (DataTable's Checkbox has no size prop).
            checkboxSx: {
              ...checkedCheckboxSx,
              "& .MuiSvgIcon-root": { fontSize: 20 },
            },
            headerAriaLabel: "Select all emails",
            getRowAriaLabel: (email) =>
              `Select ${email.category || email.subject}`,
          }}
          showEmptyRow={sortedEmails.length === 0}
          emptyRowContent={
            <div className="px-1 py-5 text-center text-sm font-semibold text-gray-500">
              No email communications match the current filters.
            </div>
          }
          // Geometry overrides keep the page's existing table chrome
          // (rounded-xl gray-200 container, gray-100 body dividers, gray-200
          // header divider, px-4 cells, header py-4 / body py-5, no row
          // hover).
          containerClassName="overflow-x-auto rounded-xl border border-gray-200 bg-white"
          tableSx={{
            minWidth: 1260,
            tableLayout: "fixed",
            width: "100%",
            "& .MuiTableCell-root": {
              borderColor: "#F3F4F6",
              px: 2,
              py: 2.5,
              verticalAlign: "middle",
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              borderColor: "#E5E7EB",
              hyphens: "none",
              overflowWrap: "normal",
              wordBreak: "normal",
              whiteSpace: "normal",
              py: 2,
            },
          }}
          headClassName="bg-gray-50"
          rowClassName="align-middle"
        />
      </section>

      {/* Single page-level Actions menu (RowActionMenu usage contract — flat
          MenuItem/Divider children fix the old wrapper-div keyboard-nav
          defect). paperSx/destructiveItemSx keep this page's existing chrome
          (#EF4444 Delete, 280px paper, heavy shadow) over the ManageEvents
          defaults. */}
      <RowActionMenu
        actions={actionMenu ? getEmailRowActions(actionMenu.email) : []}
        anchorEl={actionMenu?.el ?? null}
        open={Boolean(actionMenu)}
        onClose={() => setActionMenu(null)}
        onAction={(key) => {
          if (!actionMenu) {
            return;
          }
          const email = actionMenu.email;
          if (key === "duplicate") {
            void duplicateAsDraft(email);
            return;
          }
          if (key === "delete") {
            // Destructive action: confirm before deleting (shared module
            // delete-confirmation pattern; 13_DESIGN_JUDGMENT).
            setPendingDeleteEmail(email);
            return;
          }
          // preview / edit / schedule / viewAnalytics / viewOpenList all open
          // the saved record (unchanged behavior).
          openEmail(email);
        }}
        paperSx={{
          mt: 1,
          borderRadius: "8px",
          minWidth: 280,
          py: 0.5,
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.22)",
        }}
        destructiveItemSx={{ color: "#EF4444", fontWeight: 700 }}
      />

      {pendingDeleteEmail ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-md rounded-xl bg-white p-8 text-center shadow-2xl">
            <button
              type="button"
              onClick={() => setPendingDeleteEmail(null)}
              aria-label="Close"
              className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full bg-red-500 text-white shadow transition hover:bg-red-600"
            >
              ✕
            </button>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white">
              <DeleteOutlineIcon sx={{ fontSize: 34 }} />
            </span>
            <h3 className="mt-4 text-[22px] font-extrabold text-gray-950">
              Delete Communication?
            </h3>
            <p className="mt-2 text-[15px] leading-6 text-gray-600">
              {`"${
                pendingDeleteEmail.category || pendingDeleteEmail.subject
              }" will be removed from your email communications.`}
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                type="button"
                onClick={() => {
                  const emailToDelete = pendingDeleteEmail;
                  setPendingDeleteEmail(null);
                  archiveEmail(emailToDelete);
                }}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md bg-red-500 px-6 text-[14px] font-extrabold text-white transition hover:bg-red-600"
              >
                <DeleteOutlineIcon fontSize="small" />
                DELETE
              </button>
              <button
                type="button"
                onClick={() => setPendingDeleteEmail(null)}
                className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-gray-900 px-6 text-[14px] font-extrabold text-white transition hover:bg-black"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ManageEmails;
